import React, { useMemo, useCallback } from 'react';
import {
	BACKGROUND_THEMES,
	GREY_META,
} from '@jira/admin-pages__system-announcement-banner/src/backgroundThemes.constants.tsx';
import { useAnnouncementBannerSettingsV4Resource } from '@jira/admin-pages__system-announcement-banner-resources/src/useAnnouncementBannerSettingsV4Resource.tsx';
import { useUserDismissActions } from '@jira/admin-pages__system-announcement-banner-resources/src/useUserDismissActions.tsx';
import { graphql, useFragment } from 'react-relay';
import type { DocNode } from '@atlaskit/adf-schema';
import {
	useAnalyticsEvents,
	fireUIAnalytics,
	FireScreenAnalytics,
	ContextualAnalyticsData,
	BANNER,
} from '@atlassian/jira-product-analytics-bridge';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { functionWithCondition } from '@atlassian/jira-feature-flagging-utils';
import { fg } from '@atlassian/jira-feature-gating';
import type { BannerAnnouncement$key } from './__generated__/BannerAnnouncement.graphql';
import { Announcement } from './announcement/index.tsx';
import { Content } from './content/index.tsx';

const BannerAnnouncementOld = () => {
	const { data, update } = useAnnouncementBannerSettingsV4Resource();

	const analyticsAttributes = useMemo(
		() => ({
			visibility: data?.visibility,
			isDismissable: data?.isDismissable,
		}),
		[data?.isDismissable, data?.visibility],
	);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const [, { set: dismiss }] = useUserDismissActions();
	const accountId = useAccountId();
	const onDismiss = useCallback(() => {
		if (accountId != null && data?.hashId != null) {
			update((currentData) => (currentData ? { ...currentData, isDismissed: true } : currentData));
			dismiss(accountId, data?.hashId);
			fireUIAnalytics(
				createAnalyticsEvent({
					action: 'clicked',
					actionSubject: 'button',
				}),
				'closeAnnouncementBanner',
			);
		}
	}, [accountId, createAnalyticsEvent, data?.hashId, dismiss, update]);

	// Fallback on grey background theme if no colour found
	const backgroundTheme =
		BACKGROUND_THEMES.find((theme) => theme.name === data?.bgColor) ?? GREY_META;

	return data ? (
		<ContextualAnalyticsData sourceName="announcement" sourceType={BANNER}>
			<Announcement
				isDismissable={Boolean(data?.isDismissable)}
				onDismiss={onDismiss}
				backgroundTheme={backgroundTheme}
			>
				{/* eslint-disable-next-line @typescript-eslint/consistent-type-assertions */}
				<Content content={JSON.parse(data.content) as DocNode} />
			</Announcement>
			<FireScreenAnalytics attributes={analyticsAttributes} />
		</ContextualAnalyticsData>
	) : null;
};

type Props = {
	jiraQueryFragment?: BannerAnnouncement$key | null;
};

const BannerAnnouncementRelay = ({ jiraQueryFragment }: Props) => {
	const rawData = useFragment(
		graphql`
			fragment BannerAnnouncement on JiraQuery {
				announcementBanner(cloudId: $cloudId) @optIn(to: "JiraAnnouncementBanner") {
					# isDismissed
					isDismissible
					# isDisplayed
					# isEnabled
					message
					visibility
				}
			}
		`,
		jiraQueryFragment,
	);
	// @ts-expect-error TODO JPX-458 JPX-459 get all needed props
	const { visibility, isDismissible, hashId, bgColor, message } = rawData?.announcementBanner ?? {};
	// TODO JPX-458: get actual ADF content
	const content: DocNode = useMemo(() => {
		try {
			return message && message.length ? JSON.parse(message) : message;
		} catch {
			return message;
		}
	}, [message]);

	const analyticsAttributes = useMemo(
		() => ({
			visibility,
			isDismissable: isDismissible,
		}),
		[isDismissible, visibility],
	);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	// TODO JPX-493: replace with banner dismissal mutation
	const [, { set: dismiss }] = useUserDismissActions();
	const accountId = useAccountId();
	const onDismiss = useCallback(() => {
		if (accountId != null && hashId != null) {
			dismiss(accountId, hashId);
			fireUIAnalytics(
				createAnalyticsEvent({
					action: 'clicked',
					actionSubject: 'button',
				}),
				'closeAnnouncementBanner',
			);
		}
	}, [accountId, createAnalyticsEvent, hashId, dismiss]);

	// Fallback on grey background theme if no colour found
	const backgroundTheme = BACKGROUND_THEMES.find((theme) => theme.name === bgColor) ?? GREY_META;

	return rawData?.announcementBanner ? (
		<ContextualAnalyticsData sourceName="announcement" sourceType={BANNER}>
			<Announcement
				isDismissable={Boolean(isDismissible)}
				onDismiss={onDismiss}
				backgroundTheme={backgroundTheme}
			>
				<Content content={content} />
			</Announcement>
			<FireScreenAnalytics attributes={analyticsAttributes} />
		</ContextualAnalyticsData>
	) : null;
};

export const BannerAnnouncement: typeof BannerAnnouncementRelay = functionWithCondition(
	() => fg('jpx-459-announcement-banner-via-spa-query'),
	BannerAnnouncementRelay,
	BannerAnnouncementOld,
);
